import { HeaderProvider } from 'hooks/useHeader';
import { useLinguiI18n } from 'hooks/useLinguiI18n';
import { useTaxonomy } from 'hooks/useTaxonomy';
import React, { lazy, Suspense, useEffect } from 'react';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import { getSingleTrackRoutePaths } from 'services/libraryItem/singleTrackRouting';
import { loadCatalogForLocale } from '../locales/bootstrapLingui';
import {
  getBaseLocale,
  getSessionLanguage,
  setSessionLanguage
} from '../locales/helpers';
import { BaseLocales, Locales } from '../locales/i18nLingui';
import DevelopmentRoute from './DevelopmentRoute';
// import FullpageSpinner from 'components/FullpageSpinner';

const I18nWrapper = lazy(() => import('hooks/useI18n'));

const PageNotFound = lazy(() => import('components_2/pageNotFound'));

const ResetPassword = lazy(() => import('views_2/ResetPassword'));
const CoursesSlug = lazy(() => import('views_2/Course/details'));
const Player = lazy(() => import('views_2/Player'));
const LibraryItemDetails = lazy(
  () =>
    import(
      /* webpackChunkName: "library-item-details" */ 'screens/LibraryItemPage/details'
    )
);
const BrowsePage = lazy(() => import('screens/LibraryItemPage/browse'));
const TeacherDetails = lazy(
  () => import('screens/PublisherProfile/PublisherProfileForeignPage')
);
const IntlTopicsPage = lazy(() => import('views_2/Topics/intl/index'));
const MemberPlusV2 = lazy(() => import('views_2/MemberPlus/v2'));

const FunnelHomeIntl = lazy(() => import('views_2/FunnelHome/intl'));
const CoursesIntl = lazy(() => import('views_2/Course/browse/intl'));

const FreeTrial = lazy(() => import('components/FreeTrial'));

const IntlTopicDetails = lazy(() => import('views_2/IntlTopicDetails'));

const withI18nWrapper = <P extends object>(Component: React.ComponentType<P>) =>
  function WithI18nWrapper(props: P) {
    return (
      <Suspense fallback={<></>}>
        <I18nWrapper>
          <Component {...props} />
        </I18nWrapper>
      </Suspense>
    );
  };

const IntlTopicDetailsWithI18n = withI18nWrapper(IntlTopicDetails);
const IntlTopicPageWithI18n = withI18nWrapper(IntlTopicsPage);

const IntlPages = ({ match }: RouteComponentProps<{ lang: string }>) => {
  const { lang } = match.params;
  const i18n = useLinguiI18n();
  const currLang =
    match.params.lang === 'br'
      ? BaseLocales.BrazilianPortuguese
      : (match.params.lang as Locales);

  // eslint-disable-next-line no-shadow
  const baseLocale = getBaseLocale(currLang);
  const taxonomy = useTaxonomy(currLang);
  const singleTrackRoutePaths = getSingleTrackRoutePaths(baseLocale);

  useEffect(() => {
    if (lang) {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  useEffect(() => {
    const loadMessages = async () => {
      if (i18n.locale === baseLocale) {
        return;
      }

      try {
        const messages = await loadCatalogForLocale(baseLocale);
        i18n.load(baseLocale, messages);
        i18n.activate(baseLocale);
      } catch (e) {
        console.warn(e);
      }
    };

    loadMessages();
  }, [baseLocale, i18n]);

  useEffect(() => {
    const sessionLanguage = getSessionLanguage();
    if (!sessionLanguage) {
      setSessionLanguage(currLang);
    }
  }, [currLang]);

  return (
    <Suspense fallback={<></>}>
      <Switch>
        <Route
          exact
          path={`/:lang(es|fr|de|ru|br|it|nl)/${taxonomy.config.play}`}
          component={Player}
        />
        <Route
          exact
          path={`/:lang(es|fr|de|br|nl)/${taxonomy.config.freeTrial}`}
          component={DevelopmentRoute(FreeTrial)}
        />
        <Route>
          <HeaderProvider intlHeader>
            <Suspense fallback={<></>}>
              <Switch>
                <Route
                  exact
                  path={`${match.path}`}
                  component={FunnelHomeIntl}
                />
                <Route
                  exact
                  path={[
                    `${match.path}/meditation-topics/:slug`,
                    `${match.path}/topics/:slug`
                  ]}
                  component={IntlTopicDetailsWithI18n}
                />
                <Route path="/:lang(es|fr|de|ru|br|it|nl)">
                  <Switch>
                    <Route
                      exact
                      path={`${match.path}/${taxonomy.config.meditationCourses}`}
                      component={CoursesIntl}
                    />

                    <Route
                      exact
                      path={`${match.path}/${taxonomy.config.guidedMeditations}`}
                      component={BrowsePage}
                    />

                    <Route
                      exact
                      path={singleTrackRoutePaths.all.map(
                        segment => `${match.path}/:teacher/${segment}/:slug`
                      )}
                      component={LibraryItemDetails}
                    />

                    <Route
                      exact
                      path={`${match.path}/${taxonomy.config.meditationCourses}/:slug`}
                      render={props => {
                        return <CoursesSlug {...props} />;
                      }}
                    />

                    <Route
                      exact
                      path={`${match.path}/${taxonomy.config.meditationTeachers}/:slug`}
                      component={TeacherDetails}
                    />

                    <Route
                      exact
                      path={`${match.path}/${taxonomy.config.meditationTopics}/:slug`}
                      render={props => {
                        return (
                          <IntlTopicPageWithI18n
                            slug={`${match.params.lang}--${taxonomy.config.meditationTopics}--${props.match.params.slug}`}
                          />
                        );
                      }}
                    />

                    <Route
                      exact
                      path={`${match.path}/${taxonomy.config.resetPassword}`}
                      component={ResetPassword}
                    />

                    <Route
                      exact
                      path={`${match.path}/member-plus`}
                      component={DevelopmentRoute(MemberPlusV2)}
                    />
                    <Route component={PageNotFound} />
                  </Switch>
                </Route>
                <Route component={PageNotFound} />
              </Switch>
            </Suspense>
          </HeaderProvider>
        </Route>
      </Switch>
    </Suspense>
  );
};

export default IntlPages;
